import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoIcon from "@material-ui/icons/Info";
import { useFormik } from "formik";
import * as Yup from "yup";
import { formatearFecha } from "src/utils/formatTime";
import { makeStyles } from "@material-ui/styles";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "500px",
      maxWidth: "600px",
    },
  },
  dialogTitle: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    "& .MuiTypography-root": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
  },
  warningBox: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  infoBox: {
    backgroundColor: theme.palette.info.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  abonosContainer: {
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  abonoItem: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  totalAbonado: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  actionsContainer: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
}));

/**
 * Modal de anulación de facturas con flujo de 2 pasos
 * PASO 1: Ingresar motivo de anulación
 * PASO 2: Confirmar eliminación de abonos (si los hay)
 */
export default function ModalAnularFactura({
  open,
  onClose,
  factura,
  onAnular,
  isLoading = false,
}) {
  const classes = useStyles();
  const [paso, setPaso] = useState(1); // 1: Motivo, 2: Confirmación abonos
  const [datosAbonos, setDatosAbonos] = useState(null);

  const validationSchema = Yup.object().shape({
    motivoAnulacion: Yup.string().max(
      500,
      "El motivo no puede exceder 500 caracteres",
    ),
  });

  const formik = useFormik({
    initialValues: {
      motivoAnulacion: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (paso === 1) {
        // Primera llamada: verificar si tiene abonos
        const resultado = await onAnular({
          ...values,
          confirmarEliminacionAbonos: false,
        });

        // Si requiere confirmación, pasar al paso 2
        if (resultado?.requiereConfirmacion) {
          setDatosAbonos(resultado.abonos);
          setPaso(2);
        } else {
          // No tiene abonos, cerrar modal (éxito manejado por el padre)
          handleClose();
        }
      } else if (paso === 2) {
        // Segunda llamada: confirmar eliminación de abonos
        await onAnular({
          ...values,
          confirmarEliminacionAbonos: true,
        });
        handleClose();
      }
    },
  });

  const { values, errors, touched, handleChange, handleSubmit, resetForm } =
    formik;

  const handleClose = () => {
    resetForm();
    setPaso(1);
    setDatosAbonos(null);
    onClose();
  };

  const handleVolver = () => {
    setPaso(1);
    setDatosAbonos(null);
  };

  // Formatear montos
  const formatMonto = (monto) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(monto);
  };

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!open) {
      resetForm();
      setPaso(1);
      setDatosAbonos(null);
    }
  }, [open, resetForm]);

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <ErrorOutlineIcon />
        {paso === 1 ? "Anular Factura" : "Confirmar Anulación"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent className={classes.dialogContent}>
          {/* PASO 1: Ingresar motivo */}
          {paso === 1 && (
            <>
              <Box className={classes.warningBox}>
                <WarningIcon color="inherit" />
                <Typography variant="body2">
                  <strong>Atención:</strong> Esta acción generará una Nota de
                  Crédito (DTE tipo 61) y cambiará el estado de la factura a
                  ANULADO. Esta acción no se puede deshacer.
                </Typography>
              </Box>

              <Box className={classes.infoBox}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Información de la Factura:</strong>
                </Typography>
                <Typography variant="body2">
                  <strong>Folio:</strong> {factura?.folio}
                </Typography>
                <Typography variant="body2">
                  <strong>Cliente:</strong> {factura?.nombre || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Monto:</strong>{" "}
                  {formatMonto(factura?.valorPesos || factura?.total || 0)}
                </Typography>
                <Typography variant="body2">
                  <strong>Tipo:</strong>{" "}
                  <Chip
                    label={factura?.tipo || "N/A"}
                    size="small"
                    color="primary"
                  />
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Motivo de Anulación (opcional)"
                name="motivoAnulacion"
                value={values.motivoAnulacion}
                onChange={handleChange}
                error={
                  touched.motivoAnulacion && Boolean(errors.motivoAnulacion)
                }
                helperText={
                  touched.motivoAnulacion && errors.motivoAnulacion
                    ? errors.motivoAnulacion
                    : "Si no se ingresa un motivo, se usará 'Anulación de factura' por defecto"
                }
                variant="outlined"
                disabled={isLoading}
                inputProps={{
                  maxLength: 500,
                }}
              />

              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: 8, display: "block", textAlign: "right" }}
              >
                {values.motivoAnulacion.length}/500 caracteres
              </Typography>
            </>
          )}

          {/* PASO 2: Confirmar eliminación de abonos */}
          {paso === 2 && datosAbonos && (
            <>
              <Alert severity="error" style={{ marginBottom: 16 }}>
                <Typography variant="body2">
                  <strong>¡Importante!</strong> Esta factura tiene abonos
                  registrados que serán <strong>dados de baja</strong> al
                  anular.
                </Typography>
              </Alert>

              <Box className={classes.infoBox}>
                <Typography variant="body2" gutterBottom>
                  <strong>Motivo de anulación:</strong>
                </Typography>
                <Typography variant="body2" style={{ fontStyle: "italic" }}>
                  "{values.motivoAnulacion}"
                </Typography>
              </Box>

              <Divider style={{ margin: "16px 0" }} />

              <Box className={classes.abonosContainer}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <InfoIcon color="primary" fontSize="small" />
                  Abonos que serán dados de baja ({datosAbonos.cantidad})
                </Typography>

                <List dense>
                  {datosAbonos.abonos.map((abono, index) => (
                    <ListItem key={index} className={classes.abonoItem}>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{formatMonto(abono.monto)}</strong> -{" "}
                            {abono.metodoPago || "Método no especificado"}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            Fecha: {formatearFecha(abono.fecha, "DD/MM/YYYY")}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Box className={classes.totalAbonado}>
                  <Typography variant="subtitle2">
                    <strong>Total abonado:</strong>
                  </Typography>
                  <Typography variant="h6" color="primary">
                    <strong>{formatMonto(datosAbonos.totalAbonado)}</strong>
                  </Typography>
                </Box>
              </Box>

              <Alert severity="warning" style={{ marginTop: 16 }}>
                Al confirmar, estos abonos se darán de baja y dejarán de contar
                para la factura. La baja queda registrada con su autor y su
                motivo.
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions className={classes.actionsContainer}>
          {/* Botones PASO 1 */}
          {paso === 1 && (
            <>
              <Button onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={isLoading}
                disabled={isLoading}
              >
                Continuar
              </LoadingButton>
            </>
          )}

          {/* Botones PASO 2 */}
          {paso === 2 && (
            <>
              <Button onClick={handleVolver} disabled={isLoading}>
                Volver
              </Button>
              <Button onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="secondary"
                loading={isLoading}
                disabled={isLoading}
              >
                Confirmar Anulación
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
